import * as Cesium from 'cesium';
import { EXTENT_MIN_PADDING, EXTENT_PADDING_RATIO, SCALE_RANGE_MAX_HEIGHT, SCALE_RANGE_MIN_HEIGHT, VIEW_PADDING_RATIO } from './constants';
import type { GeoJsonFeature, LayerBounds, MapLayer } from '../../types/CommonMapTypes';

export interface DegreeBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface ViewBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

// A layer's saved extent, when finite with min < max; otherwise null.
export const toLayerBounds = (layer: LayerBounds): DegreeBounds | null => {
  const [minX, minY, maxX, maxY] = [layer.minX, layer.minY, layer.maxX, layer.maxY].map((v) => (v == null ? NaN : Number(v)));
  if (![minX, minY, maxX, maxY].every(Number.isFinite) || minX >= maxX || minY >= maxY) return null;
  return { minX, minY, maxX, maxY };
};

// Bounds the camera can fit: finite, within the world, and not the 0,0,0,0 the API saves when it could not compute them.
// Unlike toLayerBounds, a single point or a straight line (zero size on an axis) is allowed.
const toFitBounds = (layer: LayerBounds): DegreeBounds | null => {
  const [minX, minY, maxX, maxY] = [layer.minX, layer.minY, layer.maxX, layer.maxY].map((v) => (v == null ? NaN : Number(v)));
  if (![minX, minY, maxX, maxY].every(Number.isFinite) || minX > maxX || minY > maxY) return null;
  if (!minX && !minY && !maxX && !maxY) return null;
  if (minX < -180 || maxX > 180 || minY < -90 || maxY > 90) return null;
  return { minX, minY, maxX, maxY };
};

// The combined extent of the given layers' bounds, with a margin so edge assets aren't cut off; null when none has a valid extent.
export const unionLayerBounds = (layers: LayerBounds[]): DegreeBounds | null => {
  const union = layers.reduce<DegreeBounds | null>((result, layer) => {
    const bounds = toFitBounds(layer);
    if (!bounds) return result;
    if (!result) return bounds;
    return {
      minX: Math.min(result.minX, bounds.minX),
      minY: Math.min(result.minY, bounds.minY),
      maxX: Math.max(result.maxX, bounds.maxX),
      maxY: Math.max(result.maxY, bounds.maxY),
    };
  }, null);
  if (!union) return null;
  const padX = Math.max((union.maxX - union.minX) * EXTENT_PADDING_RATIO, EXTENT_MIN_PADDING);
  const padY = Math.max((union.maxY - union.minY) * EXTENT_PADDING_RATIO, EXTENT_MIN_PADDING);
  return { minX: union.minX - padX, minY: union.minY - padY, maxX: union.maxX + padX, maxY: union.maxY + padY };
};

export const isPointLayer = (layer: MapLayer) => !!layer.layerType?.toLowerCase().includes('point');

// Map Portal draws clustered point layers client-side only, with no WMS tiles.
export const isClientRenderedCluster = (layer: MapLayer) => !!layer.clustering && isPointLayer(layer);

export const isLayerReady = (layer: MapLayer) => !layer.publishStatus || layer.publishStatus === 'ready';

export const isInScaleRange = (layer: MapLayer, height: number) =>
  height >= (Number(layer.scaleRange?.max) || SCALE_RANGE_MIN_HEIGHT) && height <= (Number(layer.scaleRange?.min) || SCALE_RANGE_MAX_HEIGHT);

// A view across the date line wraps (west > east), so it falls back to the full longitude band.
export const readViewBounds = (viewer: Cesium.Viewer): ViewBounds | null => {
  const rectangle = viewer.camera.computeViewRectangle();
  if (!rectangle) return null;
  const west = Cesium.Math.toDegrees(rectangle.west);
  const east = Cesium.Math.toDegrees(rectangle.east);
  const south = Cesium.Math.toDegrees(rectangle.south);
  const north = Cesium.Math.toDegrees(rectangle.north);
  return west > east ? { west: -180, east: 180, south, north } : { west, east, south, north };
};

// The bounds API's PostGIS fallback returns lowercase keys, as Map Portal's readers allow for.
type BoundsResponse = LayerBounds & Partial<Record<'minx' | 'miny' | 'maxx' | 'maxy', string | number | null>>;

// Zoom to layer's target; null for a layer with no features (0,0,0,0 or non-numeric bounds).
export const toZoomBounds = (bounds: BoundsResponse | undefined): ViewBounds | null => {
  const [minX, minY, maxX, maxY] = [
    bounds?.minX ?? bounds?.minx,
    bounds?.minY ?? bounds?.miny,
    bounds?.maxX ?? bounds?.maxx,
    bounds?.maxY ?? bounds?.maxy,
  ].map((value) => parseFloat(String(value)));
  if ([minX, minY, maxX, maxY].some(Number.isNaN) || (minX === 0 && minY === 0 && maxX === 0 && maxY === 0)) return null;
  return { west: minX, south: minY, east: maxX, north: maxY };
};

export const padBounds = ({ west, south, east, north }: ViewBounds): ViewBounds => {
  const padX = (east - west) * VIEW_PADDING_RATIO;
  const padY = (north - south) * VIEW_PADDING_RATIO;
  return {
    west: Math.max(-180, west - padX),
    east: Math.min(180, east + padX),
    south: Math.max(-90, south - padY),
    north: Math.min(90, north + padY),
  };
};

// Layers without a valid saved extent are assumed to overlap.
export const overlapsView = (layer: MapLayer, view: ViewBounds) => {
  const extent = toLayerBounds(layer);
  return !extent || (extent.minX <= view.east && extent.maxX >= view.west && extent.minY <= view.north && extent.maxY >= view.south);
};

export const containsBounds = (outer: ViewBounds, inner: ViewBounds) =>
  inner.west >= outer.west && inner.east <= outer.east && inner.south >= outer.south && inner.north <= outer.north;

export const toBboxParam = (bounds: ViewBounds) => `${bounds.west},${bounds.south},${bounds.east},${bounds.north}`;

const isFeature = (value: unknown): value is GeoJsonFeature => {
  if (typeof value !== 'object' || value === null) return false;
  const { geometry } = value as { geometry?: unknown };
  return typeof geometry === 'object' && geometry !== null && typeof (geometry as { type?: unknown }).type === 'string';
};

export const toFeatures = (response: unknown): GeoJsonFeature[] => {
  const features = (response as { features?: unknown } | null)?.features;
  return Array.isArray(features) ? features.filter(isFeature) : [];
};

// Stable per feature across refreshes: the GeoJSON id, else its coordinates.
export const featureKey = (feature: GeoJsonFeature) => (feature.id !== undefined ? String(feature.id) : JSON.stringify(feature.geometry.coordinates));

// Caps concurrent fetches; queued callers run in arrival order.
export const createFetchLimiter = (concurrency: number) => {
  let active = 0;
  const waiting: (() => void)[] = [];
  const acquire = () =>
    new Promise<void>((resolve) => {
      if (active < concurrency) {
        active += 1;
        resolve();
      } else waiting.push(resolve);
    });
  const release = () => {
    const next = waiting.shift();
    if (next) next();
    else active -= 1;
  };
  return { acquire, release };
};

const MAP_TOP_CORNER = new Cesium.Cartographic(Math.PI, Cesium.Math.PI_OVER_TWO);

// 2D only. For automatic camera moves: shrinks the view to the map's height and
// keeps it inside the latitude range, so no empty band shows above or below.
// Manual zoom is left alone, so users can still zoom out to the whole map.
export const fitViewWithinMap2D = (viewer: Cesium.Viewer) => {
  const { scene, camera, canvas } = viewer;
  if (scene.mode !== Cesium.SceneMode.SCENE2D || canvas.width === 0 || canvas.height === 0) return;
  const maxProjectedY = scene.mapProjection.project(MAP_TOP_CORNER).y;
  const frustum = camera.frustum as Cesium.OrthographicOffCenterFrustum;

  if (frustum.top > maxProjectedY) {
    const fit = maxProjectedY / frustum.top;
    frustum.top = maxProjectedY;
    frustum.bottom = -maxProjectedY;
    frustum.right *= fit;
    frustum.left = -frustum.right;
  }

  const centreLimit = maxProjectedY - frustum.top;
  const { position } = camera;
  if (position.y > centreLimit) position.y = centreLimit;
  else if (position.y < -centreLimit) position.y = -centreLimit;
};

// 2D: the view fitViewWithinMap2D would leave after landing on `bounds`, so a flight never jumps on landing.
export const fitBoundsWithinMap2D = (viewer: Cesium.Viewer, bounds: ViewBounds): ViewBounds => {
  const { scene, canvas } = viewer;
  if (scene.mode !== Cesium.SceneMode.SCENE2D || canvas.width === 0 || canvas.height === 0) return bounds;
  const aspect = canvas.width / canvas.height;
  let width = Math.max(bounds.east - bounds.west, Cesium.Math.EPSILON7);
  let height = Math.max(bounds.north - bounds.south, Cesium.Math.EPSILON7);
  if (width / height > aspect) height = width / aspect;
  else width = height * aspect;
  if (height > 180) {
    width *= 180 / height;
    height = 180;
  }
  const centreX = (bounds.west + bounds.east) / 2;
  const centreY = Cesium.Math.clamp((bounds.south + bounds.north) / 2, height / 2 - 90, 90 - height / 2);
  return { west: centreX - width / 2, east: centreX + width / 2, south: centreY - height / 2, north: centreY + height / 2 };
};
